import type { ApiResponse } from './run-api-get'

import { useToast } from '@shared/ui/toast'

import { getAxiosErrorMessage } from './axios'

type ApiWriteMethod = 'post' | 'patch'

const DEFAULT_SUCCESS_MESSAGE = 'Request completed successfully.'
const DEFAULT_ERROR_MESSAGE = 'Unexpected error.'

interface RunApiWriteOptions {
  setLoading: (loading: boolean) => void
  method?: ApiWriteMethod
  payload?: Record<string, unknown>
}

/**
 * A write endpoint answering with the standard envelope and no data worth keeping: the server
 * message is toasted either way and the caller only learns whether it succeeded.
 */
export const runApiWrite = async (url: string | null, options: RunApiWriteOptions): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  options.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return false
    }

    const { data } = await $axios.request<ApiResponse>({
      url,
      method: options.method ?? 'post',
      data: options.payload,
    })

    if (!data.success) {
      toast.error(data.message || DEFAULT_ERROR_MESSAGE)
      return false
    }

    toast.success(data.message || DEFAULT_SUCCESS_MESSAGE)

    return true
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return false
  } finally {
    options.setLoading(false)
  }
}
